/**
 * Shared ticket list client for the lean TicketListResponse envelope
 * (sprint-8 pagination). Poll paths must omit include_total.
 */
import axios from 'axios';

export const TICKETS_PAGE_LIMIT = 20;

/** Map lean list rows to UI fields (description lives in description_preview). */
export function enrichTicketListItem(item) {
  if (!item || typeof item !== 'object') return item;
  const preview = item.description_preview ?? item.description ?? '';
  return {
    ...item,
    description: item.description ?? preview,
    description_preview: preview || null,
  };
}

export function normalizeTicketsListPayload(data) {
  if (Array.isArray(data)) {
    return {
      items: data,
      has_more: false,
      total: null,
      limit: data.length,
      offset: 0,
    };
  }
  if (data && typeof data === 'object') {
    const raw = data.items ?? data.tickets ?? data.results ?? [];
    const items = (Array.isArray(raw) ? raw : []).map(enrichTicketListItem);
    return {
      items,
      has_more: Boolean(data.has_more),
      total: typeof data.total === 'number' ? data.total : null,
      limit: typeof data.limit === 'number' ? data.limit : items.length,
      offset: typeof data.offset === 'number' ? data.offset : 0,
    };
  }
  return { items: [], has_more: false, total: null, limit: 0, offset: 0 };
}

/**
 * @param {string} apiBase - incidents API base (e.g. process.env / API constant)
 * @param {object} params
 * @param {boolean} [params.includeTotal=false] — true only on first load / filter change
 */
export async function fetchTicketSummaries(apiBase, params = {}) {
  const {
    limit = TICKETS_PAGE_LIMIT,
    offset = 0,
    includeTotal = false,
    status,
    priority,
    q,
    created_from,
    created_to,
    customization_type,
    category,
    source,
    sap_system,
    workflow_stage,
    is_error_ticket,
    signal,
  } = params;

  const query = { limit, offset };
  // Never default include_total onto pollers — only send when explicitly true.
  if (includeTotal) query.include_total = true;
  if (status) query.status = status;
  if (priority) query.priority = priority;
  if (q) query.q = q;
  if (created_from) query.created_from = created_from;
  if (created_to) query.created_to = created_to;
  if (customization_type) query.customization_type = customization_type;
  if (category) query.category = category;
  if (source) query.source = source;
  if (sap_system) query.sap_system = sap_system;
  if (workflow_stage) query.workflow_stage = workflow_stage;
  if (typeof is_error_ticket === 'boolean') query.is_error_ticket = is_error_ticket;

  const response = await axios.get(`${apiBase}/tickets`, { params: query, signal });
  return normalizeTicketsListPayload(response.data);
}
